import { TFunction } from 'i18next';
import { match, P } from 'ts-pattern';

import { MetricDetails } from '../../../../api/metrics/types';
import {
  DomainPreferences,
  MetricResult,
  SQLFilters,
} from '../../../../api/types';
import {
  CalendarYearMonthlyTimeSelectionInput,
  CalendarYearSingleValueByMonthsSegment,
  CalendarYearSingleValueByYearsSegment,
  FinancialYearQuarterlyTimeSelectionInput,
  FinancialYearSingleValueByYearsSegment,
  MetricId,
} from '../../../../api/types-graphql';
import {
  Languages,
  UndefinedValue,
  UNDEFINED_DISPLAY_KEY,
} from '../../../../constants';
import {
  dateShiftedByMonths,
  dateShiftedByYears,
  format,
  getMomentDate,
  removeMonth,
  removeYear,
} from '../../../../date/date-utils';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  CalendarYearSingleValueByYearsTimeSelection,
  DataFieldWithDataType,
  FinancialYearSingleValueByQuartersTimeSelection,
  FinancialYearSingleValueByYearsTimeSelection,
  MetricIdType,
  MetricIdTypeArrays,
  OverTimeTimeSelection,
  SegmentBy,
  SingleValueTimeSelection,
  TimeSelectionType,
} from '../../../../types';
import { percentage, sortFieldValues } from '../../../../utils';
import {
  formatMetricValue,
  formatPercentageNumber,
} from '../../../../utils-display';
import { DataFieldValueFormatter, extractSegmentValue } from '../../../hooks';
import { SortTypes } from '../../../types';
import { getDeltaSign, removeDuplicates } from '../../../utils';
import { Granularity, TimeSliderState } from '../../timeslider/types';
import {
  MultiDatasetChartData,
  SingleDatasetChartData,
} from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import { getBarChartTableDataFromChartData as getBarChartTableDataFromChartDataUtil } from '../../ui-components/data-viz-ui/charts/common/common-chart-utils/common-chart-utils';
import { getMuiLineChartTableDataFromChartData as getMuiLineChartTableDataFromChartDataUtil } from '../../ui-components/data-viz-ui/charts/line-chart/line-chart-utils';
import {
  BenchmarkPeriods,
  KpiBenchmarkConfig,
  KpiComponentSectionConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiFactboxBenchmarkValue,
  KpiFactboxSegmentedValues,
  KpiFactboxSubValue,
  KpiQueryMetaData,
  KpiSectionValue,
  MetricQueryConfig,
  QueryResponse,
  SegmentedResponse,
  TimePeriodTimeSelection,
  UnsegmentedResponse,
  ValueWithQueryMetaData,
} from './types';

export const timeSliderStateToTimePeriodTimeSelection = (
  timeSliderState: TimeSliderState
): TimePeriodTimeSelection => {
  return match(timeSliderState)
    .with(
      {
        selectedGranularity: { value: Granularity.MONTH },
        start: P.instanceOf(Date),
        end: P.instanceOf(Date),
      },
      (value) =>
        ({
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: {
            start: value.start,
            end: value.end,
          },
        }) as CalendarYearSingleValueByMonthsTimeSelection
    )
    .with(
      {
        selectedGranularity: { value: Granularity.YEAR },
        start: P.number,
        end: P.number,
      },
      (value) =>
        ({
          type: TimeSelectionType.CalendarYearSingleValueByYears,
          input: {
            start: value.start,
            end: value.end,
          },
        }) as CalendarYearSingleValueByYearsTimeSelection
    )
    .with(
      {
        selectedGranularity: { value: Granularity.FINQUARTER },
        start: { year: P.number, quarterOfYear: P.number },
        end: { year: P.number, quarterOfYear: P.number },
      },
      (value) =>
        ({
          type: TimeSelectionType.FinancialYearSingleValueByQuarters,
          input: {
            start: value.start,
            end: value.end,
          },
        }) as FinancialYearSingleValueByQuartersTimeSelection
    )
    .with(
      {
        selectedGranularity: { value: Granularity.FINYEAR },
        start: P.number,
        end: P.number,
      },
      (value) =>
        ({
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: {
            start: value.start,
            end: value.end,
          },
        }) as FinancialYearSingleValueByYearsTimeSelection
    )
    .otherwise(() => {
      throw new Error('Unsupported timeslider state');
    });
};

export const getChartDataFromSegmentedResponse = (
  response: SegmentedResponse,
  preferences: DomainPreferences,
  metricDetailsMap: Partial<Record<MetricId, MetricDetails>>,
  metricDisplayValues: Partial<Record<MetricId, string>>,
  dataFieldValueFormatter: DataFieldValueFormatter
): MultiDatasetChartData => {
  const metricDetails = metricDetailsMap[response.metric.value];
  if (!metricDetails) {
    throw new Error(
      `Invalid state. Metric details undefined for metric: ${response.metric.value}`
    );
  }
  if (!response.groups.length) {
    return {
      labels: [],
      dataMap: {},
      datasets: [],
      type: 'multi-dataset',
      isStackable: false,
    };
  }
  const { segmentByDataField } = response.groups[0];
  const datasetKeys = removeDuplicates(
    response.groups.flatMap((group) =>
      group.segmentVals.map((val) => {
        return { label: val.segmentVal ?? UndefinedValue };
      })
    )
  );
  const sortedDatasetKeys = sortFieldValues(
    datasetKeys,
    segmentByDataField,
    preferences
  ).map((l) => dataFieldValueFormatter([l.label], segmentByDataField));
  const labels = response.groups.map((group) => group.groupVal);
  const sortedLabelsAsc = sortFieldValues(
    labels.map((l) => ({ label: l ?? UndefinedValue })),
    response.groupBy.dataField,
    preferences
  ).map((l) => dataFieldValueFormatter([l.label], response.groupBy.dataField));
  const sortedLabelsDesc = sortFieldValues(
    labels.map((l) => ({ label: l ?? UndefinedValue })),
    response.groupBy.dataField,
    preferences,
    false
  ).map((l) => dataFieldValueFormatter([l.label], response.groupBy.dataField));
  const dataMap: Omit<MultiDatasetChartData, 'labels' | 'sortedLabels'> = {
    dataMap: Object.fromEntries(
      response.groups.map((group) => {
        const segmentToValueMap = group.segmentVals.map((val) => {
          return {
            datasetKey: dataFieldValueFormatter(
              [val.segmentVal],
              segmentByDataField
            ),
            value: val.value,
            valueFormatter: (val: number) => {
              return formatMetricValue(val, metricDetails.formatType);
            },
          };
        });
        return [
          dataFieldValueFormatter([group.groupVal], response.groupBy.dataField),
          segmentToValueMap,
        ];
      })
    ),
    datasets: sortedDatasetKeys.map((datasetKey) => ({
      value: datasetKey,
      valueFormatter: (val: number) => {
        return formatMetricValue(val, metricDetails.formatType);
      },
    })),
    type: 'multi-dataset',
    // True because this is for a single query response.
    // TODO: This could still be false if the query type doesn't support stacking, such as
    // attrition %. That would depend on either some config, or derived from the metric type.
    isStackable: true,
  };

  return {
    ...dataMap,
    labels: labels.map((l) =>
      dataFieldValueFormatter([l], response.groupBy.dataField)
    ),
    sortedLabels: {
      [SortTypes.ASC]: sortedLabelsAsc,
      [SortTypes.DESC]: sortedLabelsDesc,
    },
  };
};

export const getChartDataFromUnsegmentedResponse = (
  response: UnsegmentedResponse,
  preferences: DomainPreferences,
  metricDetailsMap: Partial<Record<MetricId, MetricDetails>>,
  metricDisplayValues: Partial<Record<MetricId, string>>,
  dataFieldValueFormatter: DataFieldValueFormatter
): SingleDatasetChartData => {
  const metricDetails = metricDetailsMap[response.metric.value];
  if (!metricDetails) {
    throw new Error(
      `Invalid state. Metric details undefined for metric: ${response.metric.value}`
    );
  }
  const datasetKey = response.metric.value;
  const dataMap: Omit<SingleDatasetChartData, 'labels' | 'sortedLabels'> = {
    dataMap: Object.fromEntries(
      response.groups.map((group) => {
        return [
          dataFieldValueFormatter(group.groupValue, response.groupBy.dataField),
          [
            {
              datasetKey,
              value: group.total,
            },
          ],
        ];
      })
    ),
    datasets: [
      {
        value: datasetKey,
        label: metricDisplayValues[datasetKey] ?? datasetKey,
        valueFormatter: (val: number) => {
          return formatMetricValue(val, metricDetails.formatType);
        },
      },
    ],
    type: 'single-dataset',
  };
  const labels = Object.keys(dataMap.dataMap);
  const sortedLabelsAsc = sortFieldValues(
    labels.map((l) => ({ label: l })),
    response.groupBy.dataField,
    preferences
  ).map((l) => dataFieldValueFormatter([l.label], response.groupBy.dataField));
  const sortedLabelsDesc = sortFieldValues(
    labels.map((l) => ({ label: l })),
    response.groupBy.dataField,
    preferences,
    false
  ).map((l) => dataFieldValueFormatter([l.label], response.groupBy.dataField));
  return {
    ...dataMap,
    labels: labels.map((l) =>
      dataFieldValueFormatter([l], response.groupBy.dataField)
    ),
    sortedLabels: {
      [SortTypes.ASC]: sortedLabelsAsc,
      [SortTypes.DESC]: sortedLabelsDesc,
    },
  };
};

export const getKpiFactboxBenchmarkTimeSelection = (
  mainTimeSelection: SingleValueTimeSelection,
  benchmarkPeriod: KpiFactboxBenchmarkPeriods
): SingleValueTimeSelection => {
  const newTimeSelection: SingleValueTimeSelection = match({
    mainTimeSelection,
    benchmarkPeriod,
  })
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        mainTimeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: P.select(),
        },
      },
      (input) => {
        const { start, end } = input;
        const yearOnYearTimeSelectionInput: CalendarYearSingleValueByMonthsSegment =
          {
            start: format(
              dateShiftedByYears(getMomentDate(start).valueOf(), -1)
            ),
            end: format(dateShiftedByYears(getMomentDate(end).valueOf(), -1)),
          };
        return {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: yearOnYearTimeSelectionInput,
        } as CalendarYearSingleValueByMonthsTimeSelection;
      }
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        mainTimeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: P.select(),
        },
      },
      (input) => {
        const { start, end } = input;
        const priorPeriodTimeSelectionInput: CalendarYearSingleValueByMonthsSegment =
          {
            start: format(
              dateShiftedByMonths(getMomentDate(start).valueOf(), -1)
            ),
            end: format(dateShiftedByMonths(getMomentDate(end).valueOf(), -1)),
          };
        return {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: priorPeriodTimeSelectionInput,
        } as CalendarYearSingleValueByMonthsTimeSelection;
      }
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        mainTimeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: P.select(),
        },
      },
      (input) => {
        const { start, end } = input;
        const yearOnYearTimeSelectionInput: FinancialYearSingleValueByYearsSegment =
          {
            start: start - 1,
            end: end - 1,
          };
        return {
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: yearOnYearTimeSelectionInput,
        } as FinancialYearSingleValueByYearsTimeSelection;
      }
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        mainTimeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByYears,
          input: P.select(),
        },
      },
      (input) => {
        const { start, end } = input;
        const yearOnYearTimeSelectionInput: CalendarYearSingleValueByYearsSegment =
          {
            start: start - 1,
            end: end - 1,
          };
        return {
          type: TimeSelectionType.CalendarYearSingleValueByYears,
          input: yearOnYearTimeSelectionInput,
        } as CalendarYearSingleValueByYearsTimeSelection;
      }
    )
    .otherwise(() => {
      throw new Error(
        `Not yet implemented - Benchmark Period: ${benchmarkPeriod} and main time selection ${mainTimeSelection.type}`
      );
    });
  return newTimeSelection;
};

export const getKpiSectionResponseFromQueryResults = (
  queryResponses: QueryResponse[],
  sectionConfig: KpiComponentSectionConfig,
  metricDetailsMap: Partial<Record<MetricId, MetricDetails>>,
  selectedLocale: Languages
): KpiSectionValue => {
  const { query, subQueries, benchmarkConfigs } = sectionConfig;

  const getDataFromMetricResult = (
    metricIdType: MetricIdType,
    r: MetricResult
  ): ValueWithQueryMetaData<number | null> => {
    return {
      value: (r.segments[0].groupSegments.first()?.data ?? null) as
        | number
        | null,
      queryMetaData: {
        metric: metricIdType,
        metricsSql: r.meta.metricSql ?? '',
      },
    };
  };

  const getSubValues = (
    subQueryResults: MetricResult[]
  ): ValueWithQueryMetaData<KpiFactboxSubValue>[] =>
    subQueryResults.map((r, i) => {
      const subQuery = subQueries?.[i];
      if (!subQuery) {
        throw new Error(
          'Invalid state. Sub query result found but no sub query config found'
        );
      }
      const subQueryMetricDetails =
        metricDetailsMap[subQuery.query.metric.value];
      if (!subQueryMetricDetails) {
        throw new Error(
          `Invalid state. Metric details undefined for metric: ${subQuery.query.metric}`
        );
      }
      const subQueryValueFormatter = (value: number) => {
        return formatMetricValue(value, subQueryMetricDetails.formatType);
      };
      return {
        value: {
          title: subQuery.title?.[selectedLocale] ?? '',
          value: getDataFromMetricResult(subQuery.query.metric, r).value,
          valueFormatter: subQueryValueFormatter,
        },
        queryMetaData: {
          metric: subQuery.query.metric,
          metricsSql: r.meta.metricSql ?? '',
        },
      };
    });

  // Handle subQueries-only case (when query is not provided)
  if (!query) {
    if (queryResponses.some((r) => r.error)) {
      return {
        mainValue: null,
        benchmarkValues: [],
        segmentedValues: null,
        subValues: [],
      };
    }
    const subQueryResults = (queryResponses as { data: MetricResult[] }[]).map(
      (r) => r.data[0]
    );
    return {
      title: sectionConfig.title?.[selectedLocale] ?? '',
      subtitle: sectionConfig.subtitle?.[selectedLocale] ?? '',
      mainValue: null,
      benchmarkValues: [],
      segmentedValues: null,
      subValues: getSubValues(subQueryResults),
    };
  }

  // Original code path when query exists
  const numBenchmarkQueries = benchmarkConfigs?.length ?? 0;
  const numSegmentationQueries = query.segmentBy ? 1 : 0;
  const mainQueryResponse = queryResponses[0];
  const benchmarkQueryResponses = queryResponses.slice(
    1,
    numBenchmarkQueries + 1
  );
  const segmentationQueryResponses = queryResponses.slice(
    numBenchmarkQueries + 1,
    numBenchmarkQueries + numSegmentationQueries + 1
  );
  const subQueryResponses = queryResponses.slice(
    numBenchmarkQueries + numSegmentationQueries + 1
  );
  if (
    mainQueryResponse.error ||
    benchmarkQueryResponses.some((r) => r.error) ||
    segmentationQueryResponses.some((r) => r.error) ||
    subQueryResponses.some((r) => r.error)
  ) {
    return {
      mainValue: null,
      benchmarkValues: [],
      segmentedValues: null,
      subValues: [],
    };
  }
  const mainQueryResult = mainQueryResponse.data[0];
  const benchmarkQueryResults = (
    benchmarkQueryResponses as { data: MetricResult[] }[]
  ).map((r) => r.data[0]);
  // Since segmentation has only one query result, we can just use the first one
  const segmentationQueryResult = segmentationQueryResponses.length
    ? (segmentationQueryResponses[0] as { data: MetricResult[] }).data[0]
    : null;
  const subQueryResults = (subQueryResponses as { data: MetricResult[] }[]).map(
    (r) => r.data[0]
  );

  const metricDetails = metricDetailsMap[query.metric.value];
  if (!metricDetails) {
    throw new Error(
      `Invalid state. Metric details undefined for metric: ${query.metric}`
    );
  }
  const valueFormatter = (value: number) => {
    return formatMetricValue(value, metricDetails.formatType);
  };
  const { value, queryMetaData } = getDataFromMetricResult(
    query.metric,
    mainQueryResult
  );
  const mainValue = {
    value: {
      value,
      valueFormatter,
    },
    queryMetaData,
  };
  const benchmarkValues: ValueWithQueryMetaData<KpiFactboxBenchmarkValue>[] =
    benchmarkQueryResults.map((r, i) => {
      if (!benchmarkConfigs?.[i]) {
        throw new Error(
          'Invalid state. Benchmark query result found but no benchmark config found'
        );
      }
      return {
        value: {
          benchmarkType: benchmarkConfigs[i].benchmarkType,
          benchmarkPeriod: benchmarkConfigs[i].benchmarkPeriod,
          value: getDataFromMetricResult(query.metric, r).value,
          valueFormatter,
        },
        queryMetaData: {
          metric: query.metric,
          metricsSql: r.meta.metricSql ?? '',
        },
      };
    });
  const segmentedValues: ValueWithQueryMetaData<KpiFactboxSegmentedValues> | null =
    segmentationQueryResult
      ? {
          value: {
            segmentBy: query.segmentBy?.dataField as DataFieldWithDataType,
            values: segmentationQueryResult.segments[0].groupSegments
              .filter((gs) => gs.groupSegment.length > 0)
              .map((gs) => {
                const groupSegment = gs.groupSegment[0];
                if (!groupSegment) {
                  throw new Error('Invalid state. Group segment is empty');
                }
                const rawValue = groupSegment.value ?? null;
                const segmentVal = (
                  query.segmentBy
                    ? extractSegmentValue(rawValue, query.segmentBy)
                    : rawValue
                ) as string;
                return {
                  segmentVal,
                  value: gs.data as number | null,
                };
              }),
            valueFormatter,
          },
          queryMetaData: {
            metric: query.metric,
            metricsSql: segmentationQueryResult.meta.metricSql ?? '',
          },
        }
      : null;

  const kpiSectionValue: KpiSectionValue = {
    title: sectionConfig.title?.[selectedLocale] ?? '',
    subtitle: sectionConfig.subtitle?.[selectedLocale] ?? '',
    mainValue,
    benchmarkValues,
    segmentedValues,
    subValues: getSubValues(subQueryResults),
  };
  return kpiSectionValue;
};

type MetricQueryBuilderInput = {
  metric: MetricIdType;
  timeSelection: SingleValueTimeSelection;
  filters?: SQLFilters;
  segmentations?: SegmentBy[];
};

const toMetricQueryConfig = (
  queryInput: MetricQueryBuilderInput
): MetricQueryConfig => {
  const { metric, timeSelection, filters, segmentations } = queryInput;
  return {
    metrics: [metric] as MetricIdTypeArrays,
    timeSelection,
    filters,
    ...(segmentations?.length ? { segmentations } : {}),
  };
};

/**
 * Shared low-level query builder for:
 * - main queries
 * - optional benchmark queries shifted via `getKpiFactboxBenchmarkTimeSelection`
 */
export const getMainAndOptionalBenchmarkQueries = (params: {
  mainQueries: MetricQueryBuilderInput[];
  benchmarkConfigs?: KpiBenchmarkConfig[];
  includeBenchmarks: boolean;
  benchmarkBaseQuery?: MetricQueryBuilderInput;
}): MetricQueryConfig[] => {
  const {
    mainQueries,
    benchmarkConfigs,
    includeBenchmarks,
    benchmarkBaseQuery,
  } = params;

  const mainQueryConfigs = mainQueries.map(toMetricQueryConfig);

  if (!includeBenchmarks || !benchmarkConfigs?.length || !benchmarkBaseQuery) {
    return mainQueryConfigs;
  }

  const benchmarkQueryConfigs = benchmarkConfigs.map((benchmarkConfig) =>
    toMetricQueryConfig({
      ...benchmarkBaseQuery,
      timeSelection: getKpiFactboxBenchmarkTimeSelection(
        benchmarkBaseQuery.timeSelection,
        benchmarkConfig.benchmarkPeriod
      ),
    })
  );

  return [...mainQueryConfigs, ...benchmarkQueryConfigs];
};

export const getQueriesForKpiSectionConfig = (
  sectionConfig: KpiComponentSectionConfig
): MetricQueryConfig[] => {
  const { query, benchmarkConfigs = [], subQueries = [] } = sectionConfig;

  const subQQueries = subQueries.map(({ query: subQueryQuery }) => ({
    metrics: [subQueryQuery.metric] as MetricIdTypeArrays,
    timeSelection: subQueryQuery.timeSelection,
    filters: subQueryQuery.filters,
  }));

  // If query is not provided, only return subQueries
  if (!query) {
    return subQQueries;
  }

  // For KPI main query result, we don't add segmentation in the main query.
  const mainAndBenchmarkQueries = getMainAndOptionalBenchmarkQueries({
    mainQueries: [
      {
        metric: query.metric,
        timeSelection: query.timeSelection,
        filters: query.filters,
      },
    ],
    benchmarkConfigs,
    includeBenchmarks: true,
    benchmarkBaseQuery: {
      metric: query.metric,
      timeSelection: query.timeSelection,
      filters: query.filters,
    },
  });

  const segmentationQueries = query.segmentBy
    ? [
        {
          metrics: [query.metric] as MetricIdTypeArrays,
          timeSelection: query.timeSelection,
          filters: query.filters,
          segmentations: [query.segmentBy],
        },
      ]
    : [];

  return [...mainAndBenchmarkQueries, ...segmentationQueries, ...subQQueries];
};

export const getDefaultKpiBenchmarkTitle = (
  benchmarkPeriod: KpiFactboxBenchmarkPeriods,
  benchmarkType: KpiFactboxBenchmarkTypes,
  t: TFunction
) => {
  return match({ benchmarkPeriod, benchmarkType })
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
      },
      () => t('common:benchmark.priorPeriodValue')
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
      () => t('common:benchmark.priorPeriodDelta')
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA_PERCENT,
      },
      () => t('common:benchmark.priorPeriodDeltaPercent')
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
      },
      () => t('common:benchmark.yearOnYearValue')
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
      () => t('common:benchmark.yearOnYearDelta')
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA_PERCENT,
      },
      () => t('common:benchmark.yearOnYearDeltaPercent')
    )
    .exhaustive();
};

export const getFormattedKpiBenchmarkValue = (
  benchmarkType: KpiFactboxBenchmarkTypes,
  benchmarkQueryValue: number | null,
  mainQueryValue: number | null,
  benchmarkQueryValueFormatter: (value: number) => string,
  nullValue: string
) => {
  if (benchmarkQueryValue === null) return nullValue;
  return match(benchmarkType)
    .with(KpiFactboxBenchmarkTypes.VALUE, () => {
      return benchmarkQueryValueFormatter(benchmarkQueryValue);
    })
    .with(KpiFactboxBenchmarkTypes.DELTA, () => {
      if (mainQueryValue === null) return nullValue;
      const delta = mainQueryValue - benchmarkQueryValue;
      const sign = getDeltaSign(delta);
      return `${sign}${benchmarkQueryValueFormatter(delta)}`;
    })
    .with(KpiFactboxBenchmarkTypes.DELTA_PERCENT, () => {
      if (mainQueryValue === null) return nullValue;
      const deltaPercent = percentage(
        mainQueryValue - benchmarkQueryValue,
        benchmarkQueryValue
      );
      const sign = getDeltaSign(deltaPercent);
      return `${sign}${formatPercentageNumber(deltaPercent)}`;
    })
    .exhaustive();
};

export const getBarChartTableDataFromChartData =
  getBarChartTableDataFromChartDataUtil;

export const getMuiLineChartTableDataFromChartData =
  getMuiLineChartTableDataFromChartDataUtil;

export const getOvertimeBenchmarkTimeSelection = (
  mainTimeSelection: OverTimeTimeSelection,
  benchmarkPeriod: BenchmarkPeriods
): OverTimeTimeSelection => {
  const newTimeSelection: OverTimeTimeSelection = match({
    mainTimeSelection,
    benchmarkPeriod,
  })
    .with(
      {
        mainTimeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: P.select(),
        },
        benchmarkPeriod: BenchmarkPeriods.YEAR_ON_YEAR,
      },
      (input) => {
        const { start, end } = input;
        const yearOnYearTimeSelectionInput: CalendarYearMonthlyTimeSelectionInput =
          {
            start: format(removeYear(getMomentDate(start).valueOf(), 1)),
            end: format(removeYear(getMomentDate(end).valueOf(), 1)),
          };
        return {
          type: TimeSelectionType.CalendarYearMonthly,
          input: yearOnYearTimeSelectionInput,
        } as OverTimeTimeSelection;
      }
    )
    .with(
      {
        mainTimeSelection: {
          type: TimeSelectionType.FinancialYearQuarterly,
          input: P.select(),
        },
        benchmarkPeriod: BenchmarkPeriods.YEAR_ON_YEAR,
      },
      (input) => {
        const { start, end } = input;
        const yearOnYearTimeSelectionInput: FinancialYearQuarterlyTimeSelectionInput =
          {
            start: {
              year: start.year - 1,
              quarterOfYear: start.quarterOfYear,
            },
            end: {
              year: end.year - 1,
              quarterOfYear: end.quarterOfYear,
            },
          };
        return {
          type: TimeSelectionType.FinancialYearQuarterly,
          input: yearOnYearTimeSelectionInput,
        } as OverTimeTimeSelection;
      }
    )
    .with(
      {
        mainTimeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: P.select(),
        },
        benchmarkPeriod: BenchmarkPeriods.PRIOR_PERIOD,
      },
      (input) => {
        const { start, end } = input;
        const priorPeriodTimeSelectionInput: CalendarYearMonthlyTimeSelectionInput =
          {
            start: format(removeMonth(getMomentDate(start).valueOf(), 1)),
            end: format(removeMonth(getMomentDate(end).valueOf(), 1)),
          };
        return {
          type: TimeSelectionType.CalendarYearMonthly,
          input: priorPeriodTimeSelectionInput,
        } as OverTimeTimeSelection;
      }
    )
    .otherwise(() => {
      throw new Error(
        `Not yet implemented - Benchmark Period: ${benchmarkPeriod} and main time selection ${mainTimeSelection.type}`
      );
    });
  return newTimeSelection;
};

export const getKpiUiSectionInputsFromKpiSectionValue = (
  kpiUiSectionInputs: Pick<
    KpiSectionValue,
    'mainValue' | 'benchmarkValues' | 'segmentedValues' | 'subValues'
  >,
  preferences: DomainPreferences,
  t: TFunction,
  dataFieldValueFormatter: DataFieldValueFormatter
) => {
  const getFormattedValue = (
    value: number | null,
    valueFormatter?: (value: number) => string
  ): string | null => {
    if (value === null) return null;
    const formatter = valueFormatter ?? ((num: number) => String(num));
    return formatter(value);
  };
  /**
   * mainValue semantics:
   * - undefined: no query was defined, UI should not render anything
   * - null: query was defined but response value is null, UI should render '-'
   * - string: actual formatted value to display
   */
  const mainValue =
    kpiUiSectionInputs.mainValue === null
      ? undefined
      : getFormattedValue(
          kpiUiSectionInputs.mainValue?.value.value ?? null,
          kpiUiSectionInputs.mainValue?.value.valueFormatter
        );
  const benchmarkSubValues = kpiUiSectionInputs.benchmarkValues.map((v) => {
    const { benchmarkPeriod, benchmarkType } = v.value;
    return {
      title: getDefaultKpiBenchmarkTitle(benchmarkPeriod, benchmarkType, t),
      value: getFormattedKpiBenchmarkValue(
        benchmarkType,
        v.value.value,
        kpiUiSectionInputs.mainValue?.value.value ?? null,
        v.value.valueFormatter,
        t(UNDEFINED_DISPLAY_KEY)
      ),
    };
  });
  const segmentedValues = kpiUiSectionInputs.segmentedValues;

  let segmentedSubValues: { title: string; value: string }[] = [];
  if (segmentedValues) {
    const sortedSegmentValues = sortFieldValues(
      segmentedValues.value.values
        .map((segmentedVal) => {
          const label = dataFieldValueFormatter(
            [segmentedVal.segmentVal],
            segmentedValues.value.segmentBy
          );
          return { label, segmentedVal };
        })
        .flat() ?? [],
      segmentedValues?.value.segmentBy,
      preferences
    );
    segmentedSubValues = sortedSegmentValues.map((l) => {
      return {
        title: l.label,
        value:
          getFormattedValue(
            l.segmentedVal.value,
            segmentedValues?.value.valueFormatter
          ) ?? '-',
      };
    });
  }

  const subSubValues = kpiUiSectionInputs.subValues.map((v) => {
    return {
      title: v.value.title,
      value: getFormattedValue(v.value.value, v.value.valueFormatter) ?? '-',
    };
  });
  return {
    mainValue,
    subValues: [...subSubValues, ...segmentedSubValues, ...benchmarkSubValues],
  };
};

export const extractMetaFromSection = (
  section: KpiSectionValue
): KpiQueryMetaData[] =>
  [
    section.mainValue?.queryMetaData,
    ...section.subValues.map((v) => v.queryMetaData),
    section.segmentedValues?.queryMetaData,
    ...section.benchmarkValues.map((v) => v.queryMetaData),
  ].filter(Boolean) as KpiQueryMetaData[];
